import React, { useCallback, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { PrimaryButton, InlineTextButton, EmailSpamNotice } from '../../components';
import { pathByRouteName } from '../../util/routes';
import { auth0PasswordlessStart, auth0PasswordlessVerify } from '../../util/api';
import { resolveAuth0InlineErrorMessage } from '../../util/auth0InlineErrorMessages';
import { completeAuth0FlexSessionAndNavigate } from '../../util/auth0FlexSessionNavigation';
import * as log from '../../util/log';

import css from './AuthenticationPage.module.css';

/**
 * In-app Auth0 Passwordless (email OTP): no Universal Login redirect; keeps SPA state (e.g. checkout).
 * Same return-path contract as the old redirect button (`from`, `userType`, `defaultConfirm`).
 */
const Auth0PasswordlessInlineForm = props => {
  const { from, userType } = props;
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const store = useStore();
  const routeConfiguration = useRouteConfiguration();

  const defaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration);

  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const onSendCode = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      await auth0PasswordlessStart({ email: email.trim() });
      setStep('otp');
    } catch (e) {
      setError(resolveAuth0InlineErrorMessage(e, intl, 'start'));
      log.error(e, 'auth0-passwordless-start-client');
    } finally {
      setBusy(false);
    }
  }, [email, intl]);

  const onVerify = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const { id_token: idToken } = await auth0PasswordlessVerify({
        email: email.trim(),
        otp: otp.trim(),
      });
      await completeAuth0FlexSessionAndNavigate({
        idToken,
        from,
        userType,
        defaultConfirm,
        dispatch,
        history,
        store,
        routeConfiguration,
      });
    } catch (e) {
      setError(resolveAuth0InlineErrorMessage(e, intl, 'verify'));
      log.error(e, 'auth0-passwordless-verify-client');
    } finally {
      setBusy(false);
    }
  }, [defaultConfirm, dispatch, email, from, history, intl, otp, routeConfiguration, store, userType]);

  const onBack = useCallback(() => {
    setStep('email');
    setOtp('');
    setError(null);
  }, []);

  return (
    <div className={css.passwordlessInlineRoot}>
      {step === 'email' ? (
        <>
          <label className={css.passwordlessLabel} htmlFor="fyk-passwordless-email">
            <FormattedMessage id="AuthenticationPage.passwordlessEmailLabel" />
          </label>
          <input
            id="fyk-passwordless-email"
            className={css.passwordlessInput}
            type="email"
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={busy}
          />
          <PrimaryButton
            className={css.passwordlessSubmit}
            type="button"
            onClick={onSendCode}
            disabled={busy || !email.trim()}
            inProgress={busy}
          >
            <FormattedMessage id="AuthenticationPage.passwordlessSendCode" />
          </PrimaryButton>
        </>
      ) : (
        <>
          <p className={css.passwordlessHint}>
            <FormattedMessage id="AuthenticationPage.passwordlessCodeSentHint" values={{ email }} />
          </p>
          <EmailSpamNotice variant="inline" compact />
          <label className={css.passwordlessLabel} htmlFor="fyk-passwordless-otp">
            <FormattedMessage id="AuthenticationPage.passwordlessOtpLabel" />
          </label>
          <input
            id="fyk-passwordless-otp"
            className={css.passwordlessInput}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={otp}
            onChange={e => setOtp(e.target.value)}
            disabled={busy}
          />
          <div className={css.passwordlessActions}>
            <PrimaryButton
              className={css.passwordlessSubmit}
              type="button"
              onClick={onVerify}
              disabled={busy || !otp.trim()}
              inProgress={busy}
            >
              <FormattedMessage id="AuthenticationPage.passwordlessVerify" />
            </PrimaryButton>
            <InlineTextButton
              type="button"
              onClick={onBack}
              disabled={busy}
              className={css.passwordlessBackLink}
            >
              <FormattedMessage id="AuthenticationPage.passwordlessBack" />
            </InlineTextButton>
          </div>
        </>
      )}
      {error ? <p className={css.passwordlessError}>{error}</p> : null}
    </div>
  );
};

export default Auth0PasswordlessInlineForm;
